export const JERUSALEM_TIME_ZONE = 'Asia/Jerusalem';

const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const LEGACY_DATE_PATTERN = /^\/Date\((\d+)\)\/$/;
const HEBREW_WEEKDAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

const dateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: JERUSALEM_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});

const timeFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: JERUSALEM_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23'
});

const offsetFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: JERUSALEM_TIME_ZONE,
  timeZoneName: 'longOffset',
  year: 'numeric'
});

function partsToObject(parts) {
  return Object.fromEntries(parts.filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]));
}

function pad(value) {
  return String(value).padStart(2, '0');
}

function asDate(value) {
  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'number') {
    return new Date(value);
  }

  return new Date(value ?? Date.now());
}

export function parseDateKey(dateKey) {
  if (!DATE_KEY_PATTERN.test(dateKey || '')) {
    return null;
  }

  const [year, month, day] = dateKey.split('-').map(Number);
  return { year, month, day };
}

export function makeDateKey(year, month, day) {
  return `${year}-${pad(month)}-${pad(day)}`;
}

export function getJerusalemParts(value = Date.now()) {
  const date = asDate(value);
  const dateParts = partsToObject(dateFormatter.formatToParts(date));
  const timeParts = partsToObject(timeFormatter.formatToParts(date));
  const year = Number(dateParts.year);
  const month = Number(dateParts.month);
  const day = Number(dateParts.day);

  return {
    dateKey: makeDateKey(year, month, day),
    year,
    month,
    day,
    weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
    hours: Number(timeParts.hour) % 24,
    minutes: Number(timeParts.minute),
    seconds: Number(timeParts.second)
  };
}

export function toJerusalemDateKey(value = Date.now()) {
  if (typeof value === 'string') {
    if (DATE_KEY_PATTERN.test(value)) {
      return value;
    }

    const legacyMatch = LEGACY_DATE_PATTERN.exec(value);
    if (legacyMatch) {
      return getJerusalemParts(Number(legacyMatch[1])).dateKey;
    }
  }

  if (value?.dateKey && DATE_KEY_PATTERN.test(value.dateKey)) {
    return value.dateKey;
  }

  const date = asDate(value);
  return Number.isNaN(date.getTime()) ? '' : getJerusalemParts(date).dateKey;
}

export function addDays(dateKey, amount) {
  const parts = parseDateKey(dateKey);
  if (!parts) {
    return '';
  }

  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + amount));
  return makeDateKey(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}

export function createDateRange(startDateKey, count = 8) {
  return Array.from({ length: count }, (_, index) => addDays(startDateKey, index));
}

export function getGregorianDay(dateKey) {
  const parts = parseDateKey(dateKey);
  if (!parts) {
    return null;
  }

  return {
    dateKey,
    ...parts,
    weekday: new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay()
  };
}

export function formatGregorianDate(dateKey) {
  const parts = parseDateKey(dateKey);
  return parts ? `${pad(parts.day)}/${pad(parts.month)}/${parts.year}` : '';
}

export function formatHebrewWeekday(weekday) {
  return HEBREW_WEEKDAYS[weekday] || '';
}

export function formatClock(value = Date.now()) {
  const { hours, minutes, seconds } = getJerusalemParts(value);
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

export function getJerusalemMinutes(value = Date.now()) {
  const { hours, minutes } = getJerusalemParts(value);
  return hours * 60 + minutes;
}

export function getIsraelOffsetMinutes(value = Date.now()) {
  const dateKey = value?.dateKey || value;
  const input = typeof dateKey === 'string' && DATE_KEY_PATTERN.test(dateKey)
    ? new Date(`${dateKey}T12:00:00Z`)
    : asDate(value);
  const parts = partsToObject(offsetFormatter.formatToParts(input));
  const match = /^GMT([+-])(\d{1,2})(?::(\d{2}))?$/.exec(parts.timeZoneName || '');

  if (!match) {
    return 0;
  }

  const sign = match[1] === '-' ? -1 : 1;
  return sign * (Number(match[2]) * 60 + Number(match[3] || 0));
}

export function isIsraelDst(value = Date.now()) {
  return getIsraelOffsetMinutes(value) === 180;
}

export function jerusalemDateTimeToTimestamp(dateKey, hours = 0, minutes = 0) {
  const parts = parseDateKey(dateKey);
  if (!parts) {
    return Number.NaN;
  }

  const wallClockUtc = Date.UTC(parts.year, parts.month - 1, parts.day, hours, minutes);
  let timestamp = wallClockUtc - getIsraelOffsetMinutes(wallClockUtc) * 60 * 1000;
  const correctedOffset = getIsraelOffsetMinutes(timestamp);
  timestamp = wallClockUtc - correctedOffset * 60 * 1000;
  return timestamp;
}

export function millisecondsUntilNextMinute(value = Date.now()) {
  const date = asDate(value);
  const { seconds } = getJerusalemParts(date);
  return Math.max(50, (60 - seconds) * 1000 - date.getMilliseconds());
}

export function millisecondsUntilNextJerusalemDay(value = Date.now()) {
  const now = asDate(value).getTime();
  const nextDateKey = addDays(getJerusalemParts(now).dateKey, 1);
  return Math.max(1000, jerusalemDateTimeToTimestamp(nextDateKey, 0, 0) - now);
}

export function resolveDateTimeStatus(startValue, endValue, startThresholdMinutes, now = Date.now()) {
  const start = asDate(startValue).getTime();
  const end = asDate(endValue).getTime();
  const current = asDate(now).getTime();

  if (current > end) {
    return 'past';
  }

  if (current >= start) {
    return 'now';
  }

  if (current >= start - startThresholdMinutes * 60 * 1000) {
    return 'starting';
  }

  return 'planned';
}

export function formatHoursMinutes(hours, minutes) {
  return `${pad(hours)}:${pad(minutes)}`;
}
